import { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import PersonIcon from '@mui/icons-material/Person';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import AppWrappedInput from '../../AppWrappedInput';
import { fetchSupervisingPhysicians } from '../_api';
import { LINK_FIELD_KEYS } from '../_const';

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50];
const MAX_SEARCH_RESULTS = 1000;

// Matches the state/territory options offered by the NPI Registry's own search form.
const US_STATES = [
	{ label: 'Alabama', value: 'AL' },
	{ label: 'Alaska', value: 'AK' },
	{ label: 'American Samoa', value: 'AS' },
	{ label: 'Arizona', value: 'AZ' },
	{ label: 'Arkansas', value: 'AR' },
	{ label: 'Armed Forces America', value: 'AA' },
	{ label: 'Armed Forces Europe/Canada/Middle East/Africa', value: 'AE' },
	{ label: 'Armed Forces Pacific', value: 'AP' },
	{ label: 'California', value: 'CA' },
	{ label: 'Colorado', value: 'CO' },
	{ label: 'Connecticut', value: 'CT' },
	{ label: 'Delaware', value: 'DE' },
	{ label: 'District of Columbia', value: 'DC' },
	{ label: 'Florida', value: 'FL' },
	{ label: 'Georgia', value: 'GA' },
	{ label: 'Guam', value: 'GU' },
	{ label: 'Hawaii', value: 'HI' },
	{ label: 'Idaho', value: 'ID' },
	{ label: 'Illinois', value: 'IL' },
	{ label: 'Indiana', value: 'IN' },
	{ label: 'Iowa', value: 'IA' },
	{ label: 'Kansas', value: 'KS' },
	{ label: 'Kentucky', value: 'KY' },
	{ label: 'Louisiana', value: 'LA' },
	{ label: 'Maine', value: 'ME' },
	{ label: 'Mariana Islands, Northern', value: 'MP' },
	{ label: 'Marshall Islands', value: 'MH' },
	{ label: 'Maryland', value: 'MD' },
	{ label: 'Massachusetts', value: 'MA' },
	{ label: 'Michigan', value: 'MI' },
	{ label: 'Micronesia, Federated States of', value: 'FM' },
	{ label: 'Minnesota', value: 'MN' },
	{ label: 'Mississippi', value: 'MS' },
	{ label: 'Missouri', value: 'MO' },
	{ label: 'Montana', value: 'MT' },
	{ label: 'Nebraska', value: 'NE' },
	{ label: 'Nevada', value: 'NV' },
	{ label: 'New Hampshire', value: 'NH' },
	{ label: 'New Jersey', value: 'NJ' },
	{ label: 'New Mexico', value: 'NM' },
	{ label: 'New York', value: 'NY' },
	{ label: 'North Carolina', value: 'NC' },
	{ label: 'North Dakota', value: 'ND' },
	{ label: 'Ohio', value: 'OH' },
	{ label: 'Oklahoma', value: 'OK' },
	{ label: 'Oregon', value: 'OR' },
	{ label: 'Pennsylvania', value: 'PA' },
	{ label: 'Puerto Rico', value: 'PR' },
	{ label: 'Rhode Island', value: 'RI' },
	{ label: 'South Carolina', value: 'SC' },
	{ label: 'South Dakota', value: 'SD' },
	{ label: 'Tennessee', value: 'TN' },
	{ label: 'Texas', value: 'TX' },
	{ label: 'Utah', value: 'UT' },
	{ label: 'Vermont', value: 'VT' },
	{ label: 'Virgin Islands', value: 'VI' },
	{ label: 'Virginia', value: 'VA' },
	{ label: 'Washington', value: 'WA' },
	{ label: 'West Virginia', value: 'WV' },
	{ label: 'Wisconsin', value: 'WI' },
	{ label: 'Wyoming', value: 'WY' },
];

const HEADERS = [
	{ key: 'firstName', label: 'First Name' },
	{ key: 'lastName', label: 'Last Name' },
	{ key: 'npi', label: 'NPI' },
	{ key: 'credential', label: 'Credential' },
	{ key: 'address', label: 'Address' },
	{ key: 'telephone', label: 'Telephone' },
];

/**
 * Builds the display text for a selected physician.
 * @param {Object} physician selected physician
 * @returns {string} display text
 */
export const formatPhysician = physician =>
	physician
		? [
			`${physician.firstName ?? ''} ${physician.lastName ?? ''}`.trim(),
			physician.credential,
			physician.npi ? `NPI: ${physician.npi}` : null,
		]
			.filter(Boolean)
			.join(' - ')
		: '';

/**
 * Supervising physician search input for Epic/EpicCare Link stylized to work with Custom App Request Form.
 * Prompts for first name, last name and NPI, then lets the requestor pick a single physician from the results.
 * @param {Object} props component properties
 * @param {import('formik').FormikContextType} props.formik full Formik Context
 * @param {string} props.name form field key
 * @param {string|import('react').ReactElement} props.label display text
 * @param {string|import('react').ReactElement} props.helperText helper text
 * @param {boolean} props.isRequired flag to determine whether to show required indicator
 * @returns Supervising physician search stylized for custom
 */
const SupervisingPhysicianField = ({
	formik,
	name = LINK_FIELD_KEYS.SUPERVISING_PHYSICIAN,
	label,
	helperText,
	isRequired = false,
}) => {
	const [sFirstName, setFirstName] = useState('');
	const [sLastName, setLastName] = useState('');
	const [sNpi, setNpi] = useState('');
	const [sState, setState] = useState('');
	const [sResults, setResults] = useState([]);
	const [sHasSearched, setHasSearched] = useState(false);
	const [sIsLoading, setIsLoading] = useState(false);
	const [sErrorMessage, setErrorMessage] = useState(null);
	const [sFilter, setFilter] = useState('');
	const [sPage, setPage] = useState(0);
	const [sRowsPerPage, setRowsPerPage] = useState(ROWS_PER_PAGE_OPTIONS[0]);

	const selectedPhysician = formik.values[name] || null;
	const canSearch = !!(sFirstName.trim() || sLastName.trim() || sNpi.trim());

	const filteredResults = useMemo(() => {
		const term = sFilter.trim().toLocaleLowerCase();

		if (!term) {
			return sResults;
		}

		return sResults.filter(physician =>
			HEADERS.some(header =>
				String(physician[header.key] ?? '')
					.toLocaleLowerCase()
					.includes(term),
			),
		);
	}, [sResults, sFilter]);

	const pagedResults = filteredResults.slice(
		sPage * sRowsPerPage,
		sPage * sRowsPerPage + sRowsPerPage,
	);
	const hasReachedSearchLimit = sResults.length === MAX_SEARCH_RESULTS;

	const onSearch = async () => {
		if (!canSearch) {
			return;
		}

		setIsLoading(true);
		setErrorMessage(null);
		setResults([]);
		setHasSearched(true);
		setFilter('');
		setPage(0);
		formik.setFieldValue(name, null);

		try {
			const physicians = await fetchSupervisingPhysicians({
				firstName: sFirstName,
				lastName: sLastName,
				npi: sNpi,
				state: sState,
			});
			setResults(physicians ?? []);
		} catch {
			setErrorMessage(
				'Unable to search for supervising physicians. Please try again.',
			);
		} finally {
			setIsLoading(false);
		}
	};

	const onKeyUp = e => {
		if (e.key === 'Enter') {
			onSearch();
		}
	};

	const onPhysicianSelected = physician => {
		formik.setFieldValue(name, physician);
		formik.setFieldTouched(name, true, false);
		setHasSearched(false);
		setResults([]);
		setFilter('');
		setPage(0);
	};

	const onSelectionRemoved = () => {
		formik.setFieldValue(name, null);
		formik.setFieldTouched(name, true, false);
	};

	return (
		<AppWrappedInput
			label={label}
			error={formik.touched[name] ? formik.errors[name] : null}
			isRequired={isRequired}
		>
			<FormControl variant='filled' fullWidth>
				{!selectedPhysician && (
					<Box
						sx={{
							display: 'flex',
							flexWrap: 'wrap',
							gap: 2,
							alignItems: 'flex-start',
						}}
					>
						<TextField
							variant='filled'
							label='First Name'
							value={sFirstName}
							onChange={e => setFirstName(e.target.value)}
							onKeyUp={onKeyUp}
							sx={{ flex: '1 1 180px' }}
						/>
						<TextField
							variant='filled'
							label='Last Name'
							value={sLastName}
							onChange={e => setLastName(e.target.value)}
							onKeyUp={onKeyUp}
							sx={{ flex: '1 1 180px' }}
						/>
						<TextField
							select
							variant='filled'
							label='State'
							value={sState}
							onChange={e => setState(e.target.value)}
							sx={{ flex: '1 1 180px' }}
						>
							<MenuItem value=''>
								<em>Any</em>
							</MenuItem>
							{US_STATES.map(state => (
								<MenuItem key={state.value} value={state.value}>
									{state.label}
								</MenuItem>
							))}
						</TextField>
						<TextField
							variant='filled'
							label='NPI'
							value={sNpi}
							onChange={e => setNpi(e.target.value)}
							onKeyUp={onKeyUp}
							sx={{ flex: '1 1 180px' }}
						/>
						<Button
							variant='outlined'
							onClick={onSearch}
							disabled={!canSearch || sIsLoading}
							sx={{ mt: 1 }}
						>
							Search
						</Button>
					</Box>
				)}

				{sErrorMessage && (
					<Typography color='error' sx={{ mt: 1 }}>
						{sErrorMessage}
					</Typography>
				)}

				{sIsLoading && (
					<Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
						<CircularProgress />
					</Box>
				)}

				{!sIsLoading &&
					!sErrorMessage &&
					sHasSearched &&
					!selectedPhysician &&
					hasReachedSearchLimit && (
						<Typography color='warning.main' sx={{ mt: 2 }}>
							Your search returned the maximum of 1,000 results. Try
							increasing your search specificity for better results; the
							physician you are looking for may not be included. You can also visit the <a
								href='https://npiregistry.cms.hhs.gov/search'
								target='_blank'
								rel='noopener noreferrer'
							>
								NPPES NPI Registry
							</a> for additional search capabilities that you can use to find the NPI number.
						</Typography>
					)}

				{!sIsLoading &&
					!sErrorMessage &&
					sHasSearched &&
					!selectedPhysician &&
					(sResults.length ? (
						<Box sx={{ mt: 2 }}>
							<TextField
								fullWidth
								variant='filled'
								label='Filter results'
								placeholder='Type to narrow the results below...'
								value={sFilter}
								onChange={e => {
									setFilter(e.target.value);
									setPage(0);
								}}
							/>
							<TableContainer
								component={Paper}
								sx={{ mt: 1, maxHeight: 440 }}
							>
								<Table size='small' stickyHeader>
									<TableHead>
										<TableRow>
											{HEADERS.map(header => (
												<TableCell key={header.key}>
													{header.label}
												</TableCell>
											))}
										</TableRow>
									</TableHead>
									<TableBody>
										{pagedResults.length ? (
											pagedResults.map(physician => (
												<TableRow
													hover
													key={physician.npi}
													onClick={() =>
														onPhysicianSelected(
															physician,
														)
													}
													sx={{ cursor: 'pointer' }}
												>
													{HEADERS.map(header => (
														<TableCell
															key={header.key}
														>
															{physician[header.key]}
														</TableCell>
													))}
												</TableRow>
											))
										) : (
											<TableRow>
												<TableCell
													colSpan={HEADERS.length}
												>
													No results match the filter.
												</TableCell>
											</TableRow>
										)}
									</TableBody>
								</Table>
							</TableContainer>
							<TablePagination
								component='div'
								count={filteredResults.length}
								page={sPage}
								onPageChange={(_, newPage) => setPage(newPage)}
								rowsPerPage={sRowsPerPage}
								rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
								onRowsPerPageChange={e => {
									setRowsPerPage(parseInt(e.target.value, 10));
									setPage(0);
								}}
							/>
						</Box>
					) : (
						<Typography sx={{ mt: 2 }}>
							No physicians matched your search.
						</Typography>
					))}

				{selectedPhysician && (
					<Box sx={{ mt: 2 }}>
						<Chip
							icon={<PersonIcon />}
							label={formatPhysician(selectedPhysician)}
							onDelete={onSelectionRemoved}
						/>
					</Box>
				)}

				<FormHelperText>{helperText}</FormHelperText>
			</FormControl>
		</AppWrappedInput>
	);
};

export default SupervisingPhysicianField;
